import React, { useEffect, useRef, useState } from 'react'
import { PiUser, PiUsersThree, PiHourglass, PiMoneyLight } from "react-icons/pi";
import { IoGameControllerOutline } from "react-icons/io5";
import { Line, Bar } from 'react-chartjs-2';
import { motion } from 'framer-motion';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Filler
} from 'chart.js';
import { useDispatch, useSelector } from 'react-redux';
import { getCategoryByGame, getDashboardStats, getRecentTransactions, getTopCategories, getTopGames, getTopPlatform } from '../Redux/Slice/dashboard.slice';
import { useNavigate } from 'react-router-dom';
import { getAllUsers } from '../Redux/Slice/user.slice';
import { getAllcontactUs } from '../Redux/Slice/contactUs.slice';
import { getAllBlogs } from '../Redux/Slice/blog.slice';
import { decryptData } from '../Utils/encryption';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 }
};

export default function AdminDashboard() {
  const [filter, setFilter] = useState('all');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { DashboardStats, CategoryByGame, topGames, recentTransactions, topCategories, topPlatform } = useSelector((state) => state.dashboard);
  const { allusers } = useSelector((state) => state.user);
  const { contactUs } = useSelector((state) => state.contactUs);
  const { blogs } = useSelector((state) => state.blog);

  useEffect(() => {
    dispatch(getDashboardStats(filter));
    dispatch(getCategoryByGame(filter));
    dispatch(getTopGames(filter));
    dispatch(getRecentTransactions(filter));
    dispatch(getTopCategories(filter));
    dispatch(getTopPlatform(filter));
    dispatch(getAllUsers());
    dispatch(getAllcontactUs());
    dispatch(getAllBlogs());
  }, [filter, dispatch]);

  const chartTheme = {
    color: '#9ca3af',
    gridColor: 'rgba(255, 255, 255, 0.05)',
    primary: '#6263ED',
    secondary: '#A7006F',
    font: { family: "'Chakra Petch', sans-serif", size: 12 }
  };

  const Linechartdata = {
    labels: topPlatform.map((revenue) => revenue.platform),
    datasets: [
      {
        label: 'Orders',
        data: topPlatform.map((revenue) => revenue.totalOrders),
        borderColor: chartTheme.primary,
        backgroundColor: 'rgba(98, 99, 237, 0.2)',
        borderWidth: 4,
        pointRadius: 6,
        pointBackgroundColor: '#fff',
        pointBorderColor: chartTheme.primary,
        pointBorderWidth: 3,
        pointHoverRadius: 9,
        pointHoverBackgroundColor: chartTheme.primary,
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 3,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const Linechartoptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'center',
        labels: {
          color: '#ffffff',
          font: { ...chartTheme.font, weight: '700', size: 13 },
          padding: 25,
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 10,
          boxHeight: 10,
        }
      },
      tooltip: {
        backgroundColor: 'rgba(15, 15, 15, 0.95)',
        titleColor: '#ffffff',
        titleFont: { ...chartTheme.font, weight: '700', size: 14 },
        bodyColor: '#e5e7eb',
        bodyFont: { ...chartTheme.font, size: 13 },
        padding: 16,
        cornerRadius: 15,
        displayColors: true,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        boxPadding: 8,
        usePointStyle: true,
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: chartTheme.color, font: chartTheme.font, padding: 10 }
      },
      y: {
        grid: { color: chartTheme.gridColor, drawBorder: false },
        ticks: { color: chartTheme.color, font: chartTheme.font, padding: 10, beginAtZero: true }
      }
    }
  };

  const barchartdata = {
    labels: topCategories.slice(0, 5).map((revenue) => revenue.category.categoryName),
    datasets: [
      {
        label: 'Orders',
        data: topCategories.slice(0, 5).map((revenue) => revenue.totalOrders),
        backgroundColor: chartTheme.primary,
        borderRadius: 8,
        hoverBackgroundColor: chartTheme.secondary,
      }
    ],
  };

  const barchart2data = {
    labels: CategoryByGame?.map(cat => cat.categoryName) || [],
    datasets: [
      {
        data: CategoryByGame?.map(cat => cat.gameCount) || [],
        backgroundColor: 'rgba(167, 0, 111, 0.8)',
        borderRadius: 6,
      }
    ],
  };

  const StatCard = ({ title, value, icon: Icon, onClick, color }) => (
    <motion.div
      variants={itemVariants}
      whileHover={{ y: -5, scale: 1.02 }}
      onClick={onClick}
      className="relative group cursor-pointer overflow-hidden rounded-2xl"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/[0.02] rounded-2xl border border-white/10 group-hover:border-primary/50 transition-colors duration-500" />
      <div className="relative p-6 flex items-center justify-between overflow-hidden">
        <div className="absolute -right-4 -bottom-4 opacity-10 group-hover:opacity-20 group-hover:scale-110 transition-all duration-500">
          <Icon className="text-8xl" />
        </div>
        <div>
          <p className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-1">{title}</p>
          <h3 className="text-3xl font-bold text-white tracking-tight">{value}</h3>
        </div>
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center bg-white/5 border border-white/10 text-2xl group-hover:bg-gradient-primary group-hover:text-white transition-all duration-500 overflow-hidden`}>
          <Icon />
        </div>
      </div>
    </motion.div>
  );

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-8"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Overview</h1>
          <p className="text-gray-400">Welcome back to the dashboard.</p>
        </div>
        <div className="flex items-center gap-3 bg-white/5 p-1.5 rounded-2xl border border-white/10">
          {['all', 'today', 'month', 'year'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-all duration-300 capitalize ${filter === f ? 'bg-gradient-primary text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard title="Total Users" value={DashboardStats?.totalUsers || 0} icon={PiUser} onClick={() => navigate('/admin/user')} />
        <StatCard title="Total Blogs" value={DashboardStats?.totalBlog || 0} icon={PiUsersThree} onClick={() => navigate('/admin/blog')} />
        <StatCard title="Total Games" value={DashboardStats?.totalGames || 0} icon={PiHourglass} onClick={() => navigate('/admin/games')} />
        <StatCard title="Total Revenue" value={`$${DashboardStats?.totalRevenue?.toFixed(2) || '0.00'}`} icon={PiMoneyLight} onClick={() => navigate('/admin/order')} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
        <motion.div variants={itemVariants} className="xl:col-span-2 bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
            <IoGameControllerOutline className="text-9xl" />
          </div>
          <h3 className="text-xl font-bold text-white mb-8 flex items-center gap-2">
            <span className="w-2 h-8 bg-gradient-primary rounded-full" /> Top Platforms
          </h3>
          <div className="h-[350px]">
            <Line data={Linechartdata} options={Linechartoptions} />
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-sm relative overflow-hidden group">
          <h3 className="text-xl font-bold text-white mb-8 flex items-center gap-2">
            <span className="w-2 h-8 bg-secondary rounded-full" /> Categories
          </h3>
          <div className="h-[350px]">
            <Bar
              data={barchart2data}
              options={{
                ...Linechartoptions,
                plugins: { ...Linechartoptions.plugins, legend: { display: false } },
                scales: { ...Linechartoptions.scales, x: { ...Linechartoptions.scales.x, ticks: { ...Linechartoptions.scales.x.ticks, maxRotation: 45, minRotation: 45 } } }
              }}
            />
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <motion.div variants={itemVariants} className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
          <div className="p-8 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">Top Performing Games</h3>
          </div>
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-white/[0.02]">
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Game</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Platforms</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Orders</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {topGames.slice(0, 5).map((game, index) => (
                  <tr key={index} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <img src={game?.game?.cover_image?.url} alt="" className="w-10 h-10 rounded-xl object-cover border border-white/10" />
                        <span className="text-sm font-bold text-white group-hover:text-primary transition-colors whitespace-nowrap">{game?.game?.title}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {['android', 'ios', 'windows'].map(p => (
                          game.platforms[p].count > 0 && (
                            <span key={p} className="px-2 py-1 rounded-md bg-white/5 text-[10px] font-bold text-gray-400 uppercase border border-white/5">
                              {p[0]}
                            </span>
                          )
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-medium text-gray-300">{game.totalOrders}</td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-sm font-bold text-green-400">${game.totalRevenue}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
          <div className="p-8 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">Recent Orders</h3>
            <button onClick={() => navigate('/admin/order')} className="text-xs font-bold text-primary hover:text-white transition-colors uppercase tracking-widest">View All</button>
          </div>
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-white/[0.02]">
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">ID</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Customer</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Amount</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {recentTransactions.slice(0, 5).map((order, index) => (
                  <tr key={index} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="px-6 py-4 text-sm font-mono text-gray-500">#{order.orderId.slice(-6)}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-white group-hover:text-primary transition-colors">{order.fullName}</span>
                        <span className="text-[10px] text-gray-500 truncate max-w-[150px]">{order.userEmail}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center text-sm font-bold text-white">${order.totalAmount}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${order.status === 'paid' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'}`}>
                        {order.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        <motion.div variants={itemVariants} className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
          <div className="p-8 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">Recent Registrations</h3>
            <button onClick={() => navigate('/admin/user')} className="text-xs font-bold text-primary hover:text-white transition-colors uppercase tracking-widest">View Users</button>
          </div>
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-white/[0.02]">
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">User</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Email</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[...allusers].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5).map((user, index) => (
                  <tr key={index} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center uppercase justify-center font-bold text-xs">
                          {decryptData(user?.fullName)?.[0] || 'U'}
                        </div>
                        <span className="text-sm font-bold text-white group-hover:text-primary transition-colors">{decryptData(user?.fullName)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-400">{decryptData(user?.email)}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-xs text-gray-500">{new Date(user.createdAt).toLocaleDateString()}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
          <div className="p-8 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">Recent Inquiries</h3>
            <button onClick={() => navigate('/admin/contact')} className="text-xs font-bold text-primary hover:text-white transition-colors uppercase tracking-widest">View All</button>
          </div>
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-white/[0.02]">
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">From</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Message</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[...contactUs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5).map((inquiry, index) => (
                  <tr key={index} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-white group-hover:text-primary transition-colors tracking-wide">{inquiry.firstName} {inquiry.lastName}</span>
                        <span className="text-xs tracking-wide text-gray-500">{inquiry.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-400 line-clamp-1">{inquiry.message || 'No Message'}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 rounded-md text-[9px] font-bold uppercase tracking-wider ${inquiry.isResolved ? 'bg-green-500/10 text-green-400' : 'bg-blue-500/10 text-blue-400'}`}>
                        {inquiry.isResolved ? 'Resolved' : 'New'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 pb-8">
        <motion.div variants={itemVariants} className="xl:col-span-1 bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
          <div className="p-8 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">Latest Blogs</h3>
            <button onClick={() => navigate('/admin/blog')} className="text-xs font-bold text-primary hover:text-white transition-colors uppercase tracking-widest">Manage</button>
          </div>
          <div className="p-6 space-y-6">
            {[...blogs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 4).map((blog, index) => (
              <div key={index} className="flex items-center gap-4 group cursor-pointer" onClick={() => navigate('/admin/blog')}>
                <div className="w-16 h-16 rounded-2xl bg-white/5 overflow-hidden border border-white/10 flex-shrink-0">
                  <img src={blog?.blog_images?.[0]?.url} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-bold text-white group-hover:text-primary transition-colors line-clamp-1">{blog.title}</span>
                  <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">
                    {new Date(blog.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} • {blog.author || 'Admin'}
                  </span>
                </div>
              </div>
            ))}
            {blogs.length === 0 && (
              <div className="text-center py-10">
                <p className="text-sm text-gray-500 font-medium">No blogs found</p>
              </div>
            )}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="xl:col-span-2 bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
          <div className="p-8 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">Category Performance</h3>
            <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">Top Selling</span>
          </div>
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-white/[0.02]">
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Category</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Orders</th>
                  <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {topCategories.slice(0, 5).map((revenue, index) => (
                  <tr key={index} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/5 overflow-hidden border border-white/10">
                          <img src={revenue?.category?.category_image?.url} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <span className="text-sm font-bold text-white group-hover:text-primary transition-colors">{revenue.category?.categoryName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-400 font-bold">{revenue.totalOrders}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-bold text-primary">${revenue.totalRevenue?.toFixed(2)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}
